/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Button, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, SimpleGrid } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { gql, useMutation } from '@apollo/client';
import TextFormField from '../../components/form/TextFormField';
import DateFormField from '../form/DateFormField';
import FileFormField from '../../components/form/FileFormField';
import TextAreaFormField from '../form/TextAreaFormField';
import CheckBoxFormField from '../form/CheckBoxFormField';
import GeoFormField from '../form/GeoFormField';
import * as Yup from 'yup';
import c from '../../constant';

const M_CREATE_LICENSE = gql`
	mutation createCompanyLicense(
		$companyId: String!
		$type: InsuranceSeparatedLicenseType!
		$description: String
		$isPermanentLicense: Boolean
		$licenseValidTo: DateTime
		$licenseValidFrom: DateTime
		$licenseSize: Float
		$licenseFile: Upload
		$statuteSize: Float
		$statuteFile: Upload
		$isGeoManual: Boolean
		$geoManual: String
		$geoLocation: String
		$url: String
		$displayNumber: String
		$number: String
	) {
		createCompanyLicense(
			companyId: $companyId
			type: $type
			description: $description
			isPermanentLicense: $isPermanentLicense
			licenseValidTo: $licenseValidTo
			licenseValidFrom: $licenseValidFrom
			licenseSize: $licenseSize
			licenseFile: $licenseFile
			statuteSize: $statuteSize
			statuteFile: $statuteFile
			isGeoManual: $isGeoManual
			geoManual: $geoManual
			geoLocation: $geoLocation
			url: $url
			displayNumber: $displayNumber
			number: $number
		) {
			id
		}
	}
`;

const validationSchema = Yup.object().shape({
	number: Yup.string(),
	url: Yup.string().url(),
	isGeoManual: Yup.boolean(),
	geoManual: Yup.mixed().when('isGeoManual', {
		is: (isGeoManual) => isGeoManual === true,
		then: Yup.string().required('required'),
		otherwise: Yup.string(),
	}),
	geoLocation: Yup.object().nullable(),
	statute: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize;
		} else {
			return true;
		}
	}),
	license: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize / 10;
		} else {
			return true;
		}
	}),
	licenseValidFrom: Yup.string().nullable(),
	licenseValidTo: Yup.string().nullable(),
	isPermanentLicense: Yup.boolean().nullable(),
	description: Yup.string().nullable(),
});

function AddCompanyLicenseModal({ id = null, type = null, isOpen = false, onClose = () => null, onCallback = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [isGeoManual, setGeoManual] = useState(false);
	const [isPermanentLicense, setPermanentLicense] = useState(false);
	const [createLicense, { loading, data, error }] = useMutation(M_CREATE_LICENSE);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					})
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	useEffect(() => {
		if (data) {
			if (data.createCompanyLicense) {
				toast({
					title: t('Company license added successfully'),
					status: 'success',
					duration: 5000,
					isClosable: true,
					position: 'bottom-right',
				});
				onClose();
				onCallback();
			}
		}
	}, [data]);

	const create = (values) => {
		const variables = {
			companyId: id,
			type: type,
			number: values?.number,
			url: values.url,
			isGeoManual: values?.isGeoManual,
			geoManual: values?.geoManual,
			geoLocation: JSON.stringify(values?.geoLocation),
			statuteFile: values.statute,
			statuteSize: values?.statute?.size,
			licenseFile: values.license,
			licenseSize: values?.license?.size,
			licenseValidTo: values?.licenseValidTo,
			licenseValidFrom: values?.licenseValidFrom,
			isPermanentLicense: values?.isPermanentLicense,
			description: values?.description,
		};
		createLicense({ variables });
	};

	return (
		<>
			<Modal closeOnEsc={false} closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} size="4xl">
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pb="2">
					<ModalHeader>{t('Add License')}</ModalHeader>
					<ModalCloseButton isDisabled={loading} />
					<ModalBody>
						<Formik
							initialValues={{
								number: '',
								url: '',
								isGeoManual: false,
								geoManual: '',
								geoLocation: null,
								statute: null,
								license: null,
								licenseValidFrom: null,
								licenseValidTo: null,
								isPermanentLicense: false,
								description: '',
							}}
							validationSchema={validationSchema}
							onSubmit={create}
						>
							{(props) => (
								<Form>
									<Box mt="3">
										<SimpleGrid columns={2} spacingX={6} spacingY={0}>
											<Box>
												<TextFormField {...props} label="Number" name="number" />
												<TextFormField {...props} label="URL" name="url" />
												<FileFormField {...props} label="Statute" name="statute" maxFileSize={c.maxFileSize} />
												<FileFormField {...props} label="License" name="license" maxFileSize={c.maxFileSize / 10} />
												{!isPermanentLicense && (
													<>
														<DateFormField name="licenseValidFrom" label="License Valid From" />
														<DateFormField name="licenseValidTo" label="License Valid To" />
													</>
												)}
												<CheckBoxFormField
													{...props}
													label="Is Permanent License"
													name="isPermanentLicense"
													callbackValueChange={setPermanentLicense}
												/>
											</Box>
											<Box>
												{isGeoManual ? (
													<TextAreaFormField
														{...props}
														label="Address"
														name="geoManual"
														required={true}
														buttomComponent={
															<CheckBoxFormField {...props} label="Input" name="isGeoManual" callbackValueChange={setGeoManual} />
														}
													/>
												) : (
													<GeoFormField
														{...props}
														label="Address"
														name="geoLocation"
														buttomComponent={
															<CheckBoxFormField {...props} label="Input" name="isGeoManual" callbackValueChange={setGeoManual} />
														}
													/>
												)}
												<TextAreaFormField {...props} label="Description" name="description" />
											</Box>
										</SimpleGrid>
										<Flex textAlign="right" mt="6" justifyContent="space-between" alignItems="center">
											<Button
												_hover={{ bg: 'primary.dark' }}
												id="login-btn"
												variant="solid"
												size="md"
												h="42px"
												w="128px"
												bg="primary.dark"
												color="white"
												type="submit"
												isLoading={loading}
												loadingText={t('CREATE')}
												spinnerPlacement="start"
											>
												{t(`CREATE`)}
											</Button>
										</Flex>
									</Box>
								</Form>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default AddCompanyLicenseModal;
